<template>
  <div class="w-full">
    <!-- Card 1: COD -->
    <b-card class="border rounded-xl border-gray-200 mb-3">
      <div class="flex items-center justify-between">
        <div>
          <h5 class="text-base font-semibold text-[#333333] mb-0.5">
            COD
          </h5>
          <p class="text-xs md:text-sm text-[#828282] mb-0">
            Aktifkan pembayaran Cash on Delivery (COD) agar pelanggan dapat membayar saat pesanan diterima
          </p>
        </div>
        <b-form-checkbox
          id="toggle-cod"
          :checked="isCodSelected"
          switch
          class="custom-payment-switch mb-0"
          @change="val => togglePaymentMethod('COD', val)"
        />
      </div>
    </b-card>

    <!-- Card 2: Non COD -->
    <b-card class="border rounded-xl border-gray-200 mb-3">
      <div class="flex items-center justify-between pb-1 mb-2 border-b">
        <div>
          <h5 class="text-base font-semibold text-[#333333] mb-0.5">
            Non COD
          </h5>
          <p class="text-xs md:text-sm text-[#828282] mb-0">
            Kelola pembayaran digital dan transfer bank yang tersedia bagi pelanggan.
            <a
              href="https://bantuan.komerce.id/id/article/informasi-pemotongan-biaya-pada-transaksi-jenis-pembayaran-non-cod-komchat-ai-ee1wvf/"
              target="_blank"
              class="text-[#F95031] font-medium hover:underline ml-1"
            >
              Skema Biaya
            </a>
          </p>
        </div>
        <b-form-checkbox
          id="toggle-non-cod"
          :checked="isNonCodEnabled"
          switch
          class="custom-payment-switch mb-0"
          @change="onToggleNonCod"
        />
      </div>

      <!-- Section 1: Transfer Bank -->
      <div
        v-show="isNonCodEnabled"
        class="mb-3"
      >
        <div class="flex items-center justify-between mb-1.5">
          <span class="font-medium text-sm text-[#333333]">
            Transfer Bank ({{ selectedBankCount }}/{{ bankAccount.length || 10 }})
          </span>
          <button
            type="button"
            class="text-primary text-xs md:text-sm font-medium hover:underline p-0 bg-transparent border-0 cursor-pointer"
            :disabled="onAddBank || bankAccount.length >= 10"
            @click="addBank()"
          >
            + Tambah Rekening
          </button>
        </div>

        <!-- Add Bank Modal -->
        <AddBankAccount
          :visible="onAddBank"
          :account-number="accountNumber"
          :bank-name="bankName"
          :account-owner="accountOwner"
          :id-bank="idBank"
          @refresh="getBankAccountData"
          @close="onAddBank = false"
        />

        <!-- Delete Bank Modal -->
        <ModalDeleteBankAccount
          :visible="showDeleteModal"
          :bank-data="bankToDelete"
          @deleted="onBankDeleted"
          @close="closeDeleteModal"
        />

        <!-- Bank List Grid -->
        <div
          v-if="bankAccount.length"
          class="grid grid-cols-1 md:grid-cols-2 gap-2"
        >
          <div
            v-for="(item, index) in bankAccount"
            :key="getBankId(item) || index"
            class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-between hover:border-gray-300 transition-colors"
          >
            <!-- Left: Checkbox + Bank Info -->
            <div class="flex items-center gap-2">
              <b-form-checkbox
                :id="`checkbox-bank-${index}`"
                :checked="isBankChecked(item)"
                class="custom-payment-checkbox mb-0"
                @change="val => toggleBankSelection(item, val)"
              />
              <div>
                <div class="flex items-center gap-1">
                  <span class="font-semibold text-sm text-[#1E293B]">
                    {{ item.bank_name }} • {{ item.account_no }}
                  </span>
                  <!-- Verified Icon (Blue Badge) -->
                  <svg
                    v-if="item.type !== 1"
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3.5 h-3.5 text-[#0284C7] fill-current flex-shrink-0"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <div class="text-xs text-[#828282]">
                  {{ item.account_name || item.bank_owner_name || '-' }}
                </div>
              </div>
            </div>

            <!-- Right: Action Buttons (Edit + Delete) -->
            <div class="flex items-center gap-1.5">
              <button
                type="button"
                class="text-primary text-xs md:text-sm font-medium hover:underline p-0 bg-transparent border-0 cursor-pointer"
                @click="addBank(item)"
              >
                Edit
              </button>
              <button
                type="button"
                class="p-0.5 text-[#EF4444] hover:bg-red-50 rounded border-0 bg-transparent cursor-pointer flex items-center justify-center transition-colors"
                title="Hapus Rekening"
                @click="deleteBank(item)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!onAddBank"
          class="border border-dashed border-gray-300 rounded-lg p-3 text-center"
        >
          <b-img
            width="28"
            height="28"
            center
            src="https://storage.googleapis.com/komerce/assets/icons/search-abu.svg"
            class="mb-1 opacity-60"
          />
          <div class="text-sm font-medium text-[#333333]">
            Belum ada data rekening
          </div>
          <div class="text-xs text-[#828282]">
            Tambahkan rekening bank untuk pembayaran non-COD
          </div>
        </div>
      </div>

      <!-- Section 2: Metode Pembayaran Komerce -->
      <div v-show="isNonCodEnabled">
        <div class="flex items-center gap-1 mb-1.5">
          <span class="font-medium text-sm text-[#333333]">
            Metode Pembayaran Komerce
          </span>
          <span
            id="popover-payment-komerce"
            class="cursor-pointer inline-flex items-center text-[#F97316]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <line
                x1="12"
                y1="16"
                x2="12"
                y2="12"
              />
              <line
                x1="12"
                y1="8"
                x2="12.01"
                y2="8"
              />
            </svg>
          </span>
          <b-popover
            target="popover-payment-komerce"
            triggers="hover"
            placement="top"
            custom-class="custom-popover text-xs"
          >
            Pembayaran dilakukan melalui Virtual Account (VA) atau QRIS, dan akan masuk ke saldo Kompay Anda.
          </b-popover>
        </div>

        <!-- Komerce Payment Toggle Box -->
        <div class="border border-gray-200 rounded-lg p-2 flex items-center justify-between bg-white">
          <span class="text-sm text-[#1E293B]">
            Metode Pembayaran Komerce
          </span>
          <b-form-checkbox
            :checked="isKomerceSelected"
            switch
            class="custom-payment-switch mb-0"
            :disabled="hasInvalidPrice"
            @change="toggleKomerceSwitch"
          />
        </div>

        <!-- Warning Alert -->
        <div
          v-if="hasInvalidPrice"
          class="flex items-center mt-2 bg-[#FFF2E2] gap-2 border border-[#FBA63C] rounded-lg p-2 text-[#FBA63C] text-xs"
        >
          <b-icon
            icon="exclamation-triangle"
            variant="warning"
            class="flex-shrink-0"
          />
          <div>
            Produk Anda {{ invalidPriceProducts.join(', ') }} tidak memenuhi syarat minimum transaksi sebesar Rp 10.000. Anda tidak dapat melanjutkan pembayaran menggunakan metode ini.
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import AddBankAccount from '../AddBankAccount.vue'
import ModalDeleteBankAccount from '../ModalDeleteBankAccount.vue'
import { apiGetBankAccount } from '../service/api.bankAccount.service'

export default {
  components: {
    AddBankAccount,
    ModalDeleteBankAccount,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    hasInvalidPrice: {
      type: Boolean,
      default: false,
    },
    invalidPriceProducts: {
      type: Array,
      default: () => [],
    },
    bankAccounts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      onAddBank: false,
      bankAccount: [],
      accountNumber: '',
      idBank: '',
      bankName: '',
      accountOwner: '',
      isNonCodManualToggle: null,
      showDeleteModal: false,
      bankToDelete: null,
    }
  },
  computed: {
    isCodSelected() {
      if (!Array.isArray(this.formData.payment_method)) return false
      return this.formData.payment_method.includes('COD')
    },
    isNonCodEnabled() {
      if (this.isNonCodManualToggle !== null) {
        return this.isNonCodManualToggle
      }
      if (!Array.isArray(this.formData.payment_method)) return false
      return this.formData.payment_method.some(m => ['BANK_TRANSFER', 'KOMERCE'].includes(m))
        || (Array.isArray(this.formData.bank_account_ids) && this.formData.bank_account_ids.length > 0)
    },
    isKomerceSelected() {
      if (!Array.isArray(this.formData.payment_method)) return false
      return this.formData.payment_method.includes('KOMERCE')
    },
    selectedBankCount() {
      if (!Array.isArray(this.formData.bank_account_ids)) return 0
      return this.formData.bank_account_ids.length
    },
  },
  watch: {
    bankAccounts: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length > 0) {
          const ids = newVal.map(b => Number(b.bank_account_id || b.id)).filter(Boolean)
          if (ids.length > 0 && (!this.formData.bank_account_ids || this.formData.bank_account_ids.length === 0)) {
            this.$set(this.formData, 'bank_account_ids', ids)
          }
          if (!this.bankAccount || this.bankAccount.length === 0) {
            this.bankAccount = [...newVal]
          } else {
            newVal.forEach(propBank => {
              const propId = this.getBankId(propBank)
              const exists = this.bankAccount.some(b => this.getBankId(b) === propId)
              if (!exists && propId) {
                this.bankAccount.push(propBank)
              }
            })
          }
        }
      },
    },
    'formData.payment_method': {
      handler(newVal) {
        const methods = Array.isArray(newVal) ? newVal : []
        if (methods.includes('BANK_TRANSFER')) {
          if (!this.formData.bank_account_ids || this.formData.bank_account_ids.length === 0) {
            if (Array.isArray(this.bankAccounts) && this.bankAccounts.length > 0) {
              const ids = this.bankAccounts.map(item => Number(item.bank_account_id || item.id)).filter(Boolean)
              if (ids.length > 0) {
                this.$set(this.formData, 'bank_account_ids', ids)
              }
            }
          }
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.formData.payment_method && !Array.isArray(this.formData.payment_method)) {
      this.formData.payment_method = [this.formData.payment_method]
    }
    this.getBankAccountData()
  },
  methods: {
    getBankId(item) {
      return Number(item?.bank_account_id || item?.id || item)
    },
    isBankChecked(item) {
      const id = this.getBankId(item)
      if (!id || !Array.isArray(this.formData.bank_account_ids)) return false
      return this.formData.bank_account_ids.some(bId => Number(bId) === id)
    },
    toggleBankSelection(item, isChecked) {
      const id = this.getBankId(item)
      if (!id) return
      let current = Array.isArray(this.formData.bank_account_ids)
        ? [...this.formData.bank_account_ids.map(Number)]
        : []
      if (isChecked) {
        if (!current.includes(id)) current.push(id)
      } else {
        current = current.filter(bId => bId !== id)
      }
      this.$set(this.formData, 'bank_account_ids', current)

      let methods = Array.isArray(this.formData.payment_method) ? [...this.formData.payment_method] : []
      if (current.length > 0) {
        // Turning on any bank account switches off KOMERCE
        methods = methods.filter(m => m !== 'KOMERCE')
        if (!methods.includes('BANK_TRANSFER')) {
          methods.push('BANK_TRANSFER')
        }
      } else {
        // If 0 bank accounts selected, remove BANK_TRANSFER
        methods = methods.filter(m => m !== 'BANK_TRANSFER')
      }
      this.$set(this.formData, 'payment_method', methods)
      this.$emit('validate')
    },
    togglePaymentMethod(methodName, isChecked) {
      let methods = []
      if (Array.isArray(this.formData.payment_method)) {
        methods = [...this.formData.payment_method]
      } else if (this.formData.payment_method) {
        methods = [this.formData.payment_method]
      }

      if (isChecked) {
        if (!methods.includes(methodName)) {
          methods.push(methodName)
        }
      } else {
        methods = methods.filter(m => m !== methodName)
      }
      this.$set(this.formData, 'payment_method', methods)
      this.$emit('validate')
    },
    toggleKomerceSwitch(isChecked) {
      if (isChecked && this.hasInvalidPrice) return

      let methods = Array.isArray(this.formData.payment_method) ? [...this.formData.payment_method] : []

      if (isChecked) {
        // Turn on KOMERCE -> remove BANK_TRANSFER and clear bank accounts
        methods = methods.filter(m => m !== 'BANK_TRANSFER')
        if (!methods.includes('KOMERCE')) {
          methods.push('KOMERCE')
        }
        this.$set(this.formData, 'bank_account_ids', [])
      } else {
        methods = methods.filter(m => m !== 'KOMERCE')
      }

      this.$set(this.formData, 'payment_method', methods)
      this.$emit('validate')
    },
    onToggleNonCod(enabled) {
      this.isNonCodManualToggle = enabled
      let methods = Array.isArray(this.formData.payment_method) ? [...this.formData.payment_method] : []

      if (enabled) {
        // Default on Non COD: default to KOMERCE if not having invalid price, else BANK_TRANSFER
        if (!methods.includes('BANK_TRANSFER') && !methods.includes('KOMERCE')) {
          if (!this.hasInvalidPrice) {
            methods.push('KOMERCE')
            this.$set(this.formData, 'bank_account_ids', [])
          } else {
            methods.push('BANK_TRANSFER')
            if (this.bankAccount.length) {
              this.$set(this.formData, 'bank_account_ids', this.bankAccount.map(b => this.getBankId(b)).filter(Boolean))
            }
          }
        }
      } else {
        methods = methods.filter(m => !['BANK_TRANSFER', 'KOMERCE'].includes(m))
        this.$set(this.formData, 'bank_account_ids', [])
      }
      this.$set(this.formData, 'payment_method', methods)
      this.$emit('validate')
    },
    async getBankAccountData() {
      try {
        const { data } = await apiGetBankAccount()
        const list = data.data || []
        const combined = [...list]
        if (Array.isArray(this.bankAccounts) && this.bankAccounts.length > 0) {
          this.bankAccounts.forEach(propBank => {
            const propId = this.getBankId(propBank)
            const exists = combined.some(b => this.getBankId(b) === propId)
            if (!exists && propId) {
              combined.push(propBank)
            }
          })
        }
        this.bankAccount = combined
      } catch {
        this.bankAccount = Array.isArray(this.bankAccounts) ? [...this.bankAccounts] : []
        this.$toast_error({ message: 'Gagal mengambil data bank account' })
      }
      this.$emit('validate')
    },
    addBank(item = null) {
      if (item && (item.bank_account_id || item.id)) {
        this.accountNumber = item.account_no || ''
        this.bankName = item.bank_name || ''
        this.accountOwner = item.account_name || item.bank_owner_name || ''
        this.idBank = item.bank_account_id || item.id || ''
      } else {
        this.accountNumber = ''
        this.bankName = ''
        this.accountOwner = ''
        this.idBank = ''
      }
      this.onAddBank = true
    },
    deleteBank(item) {
      this.bankToDelete = item
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.bankToDelete = null
    },
    onBankDeleted(id) {
      this.bankAccount = this.bankAccount.filter(b => this.getBankId(b) !== id)
      if (Array.isArray(this.formData.bank_account_ids)) {
        this.formData.bank_account_ids = this.formData.bank_account_ids.filter(bId => Number(bId) !== id)
        if (this.formData.bank_account_ids.length === 0 && Array.isArray(this.formData.payment_method)) {
          const methods = this.formData.payment_method.filter(m => m !== 'BANK_TRANSFER')
          this.$set(this.formData, 'payment_method', methods)
        }
      }
      this.$emit('validate')
      this.closeDeleteModal()
    },
  },
}
</script>

<style lang="scss">
.custom-payment-switch {
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}

.custom-payment-checkbox {
  .custom-control-label::before {
    border-radius: 4px !important;
    border: 1.5px solid #CBD5E1 !important;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}
.custom-payment-radio {
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #F97316 !important;
    background-color: #F97316 !important;
  }
}
</style>
